import PasswordField from '@components/v2/form/PasswordField/PasswordField';
import PhoneField from '@components/v2/form/PhoneField/PhoneField';
import TextField from '@components/v2/form/TextField/TextField';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Grid } from '@mui/material';
import { Controller, FormProvider } from 'react-hook-form';
import LangEnum from '../../lang/LangEnum';
import { StoreSignUpTestIds } from '../../utils/testIds/storeSignUp.testIds';
import { useTranslation } from '../translation/TranslationContext';
import SignUpTermsNotice from './SignUpTermsNotice';
import { useCreateAccountForm } from './hook/useCreateAccountForm';
import useRegisterStepSubmit from './hooks/useRegisterStepSubmit';
import { CreateAccountProps } from './types/createAccount.types';

const gridItemSize = { xs: 12, sm: 6 };

const CreateAccount = ({ store, isActive, ...rest }: CreateAccountProps) => {
  const { translate } = useTranslation();
  const { form, submit, handleChangeCountryCode, isSubmitting } = useCreateAccountForm({ store });

  const { control } = form;

  useRegisterStepSubmit({ submit, isSubmitting, isActive });

  return (
    <Box {...rest}>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <Grid container columnSpacing={1} rowSpacing={1.5}>
            <Grid size={12}>
              <Typography>{translate(LangEnum.SIGN_UP_NEW_ACCOUNT_SECTION)}</Typography>
            </Grid>
            <Grid size={gridItemSize}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_NAME)}
                    slotProps={{ htmlInput: { 'test-id': StoreSignUpTestIds.NEW_ACCOUNT_NAME } }}
                  />
                )}
              />
            </Grid>
            <Grid size={gridItemSize}>
              <Controller
                name="surname"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_SURNAME)}
                    slotProps={{
                      htmlInput: { 'test-id': StoreSignUpTestIds.NEW_ACCOUNT_SURNAME },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={gridItemSize}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_EMAIL)}
                    slotProps={{ htmlInput: { 'test-id': StoreSignUpTestIds.NEW_ACCOUNT_EMAIL } }}
                  />
                )}
              />
            </Grid>
            <Grid size={gridItemSize}>
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <PhoneField
                    name={field.name}
                    value={field.value ?? ''}
                    onBlur={field.onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_TELEPHONE)}
                    onChange={(value, data) => {
                      field.onChange(value);
                      handleChangeCountryCode(data.dialCode);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={gridItemSize}>
              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => (
                  <PasswordField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_PASSWORD)}
                    slotProps={{
                      htmlInput: { 'test-id': StoreSignUpTestIds.NEW_ACCOUNT_PASSWORD },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={gridItemSize}>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field, fieldState }) => (
                  <PasswordField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.CREATE_ACCOUNT_FORM_CONFIRM_PASSWORD)}
                    slotProps={{
                      htmlInput: { 'test-id': StoreSignUpTestIds.NEW_ACCOUNT_CONFIRM_PASSWORD },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12} sx={{ mt: 1.5 }}>
              <SignUpTermsNotice />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Box>
  );
};

export default CreateAccount;
